/**
 * Cloche de notifications dans l'en-tête de la console Admin.
 *
 * - Badge compteur rouge en surimpression (rafraîchi toutes les 60 s).
 * - Dropdown « Centre d'alertes » : 10 dernières notifications.
 * - Clic sur une notification → la marque comme lue + navigue vers `lien` si présent.
 * - Ferme au clic extérieur ou appui sur Echap.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { clsx } from 'clsx';

import {
  listNotifications,
  compteNonLues,
  marquerLue,
  type Notification,
} from '@/api/notifications';
import { formatDate } from '@/lib/date';
import { resolveRoute } from '@/lib/notificationRoute';

const REFETCH_INTERVAL_MS = 60_000;
const MAX_VISIBLE = 10;

export default function NotificationBell(): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Compteur seul (léger, polling 60 s).
  const countQuery = useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: compteNonLues,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });

  // Liste des notifications (chargée lors de l'ouverture).
  const listQuery = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: listNotifications,
    enabled: open,
    staleTime: 30_000,
  });

  // Mutation marquer comme lue.
  const markMutation = useMutation({
    mutationFn: marquerLue,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Fermeture au clic extérieur.
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent): void => {
      if (containerRef.current === null) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Fermeture à l'appui sur Echap.
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const count = countQuery.data ?? 0;
  const notifications = (listQuery.data ?? []).slice(0, MAX_VISIBLE);

  const handleClickNotif = (notif: Notification): void => {
    if (!notif.lu) {
      markMutation.mutate(notif.id);
    }
    const route = resolveRoute(notif.lien);
    if (route !== null) {
      setOpen(false);
      navigate(route);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label={
          count > 0
            ? `Centre d'alertes — ${count} notification${count > 1 ? 's' : ''} non lue${count > 1 ? 's' : ''}`
            : "Centre d'alertes"
        }
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-9 w-9 items-center justify-center rounded-xl border border-line bg-white text-navy-700 transition hover:border-gold-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
      >
        <Bell className="h-4 w-4" aria-hidden />
        {count > 0 && (
          <span
            aria-hidden
            className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-0.5 font-sans text-[10px] font-bold leading-none text-white"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Centre d'alertes"
          className="absolute right-0 top-12 z-50 flex w-80 flex-col overflow-hidden rounded-xl border border-line bg-white shadow-lift"
        >
          <header className="flex items-center justify-between border-b border-line px-4 py-3">
            <h3 className="font-display text-sm font-extrabold text-navy-700">
              Centre d&apos;alertes
            </h3>
            {count > 0 && (
              <span className="rounded-full bg-danger/10 px-2 py-0.5 font-sans text-[11px] font-bold text-danger">
                {count} non lue{count > 1 ? 's' : ''}
              </span>
            )}
          </header>

          <div className="max-h-96 overflow-y-auto">
            {listQuery.isLoading && (
              <p
                role="status"
                className="px-4 py-6 text-center font-sans text-xs text-sub"
              >
                Chargement…
              </p>
            )}
            {listQuery.isError && (
              <p
                role="alert"
                className="px-4 py-6 text-center font-sans text-xs text-danger"
              >
                Impossible de charger les alertes.
              </p>
            )}
            {!listQuery.isLoading && !listQuery.isError && notifications.length === 0 && (
              <p className="px-4 py-6 text-center font-sans text-xs text-sub">
                Aucune notification pour le moment.
              </p>
            )}
            {notifications.map((notif) => (
              <button
                key={notif.id}
                type="button"
                onClick={() => handleClickNotif(notif)}
                className={clsx(
                  'w-full border-t border-line px-4 py-3 text-left transition hover:bg-pearl focus:outline-none focus-visible:bg-pearl',
                  !notif.lu && 'bg-navy-50/60',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <span
                    className={clsx(
                      'flex-1 font-display text-xs font-bold leading-snug',
                      notif.lu ? 'text-ink' : 'text-navy-700',
                    )}
                  >
                    {notif.titre}
                  </span>
                  {!notif.lu && (
                    <span
                      aria-hidden
                      className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-navy-700"
                    />
                  )}
                </div>
                <p className="mt-0.5 line-clamp-2 font-sans text-xs leading-relaxed text-sub">
                  {notif.message}
                </p>
                <p className="mt-1 font-sans text-[10px] text-sub/60">
                  {formatDate(notif.created_at)}
                </p>
              </button>
            ))}
          </div>

          {notifications.length > 0 && (
            <footer className="border-t border-line px-4 py-2.5">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate('/notifications');
                }}
                className="w-full font-sans text-xs text-navy-700 underline-offset-2 hover:underline focus:outline-none focus-visible:underline"
              >
                Voir toutes les alertes
              </button>
            </footer>
          )}
        </div>
      )}
    </div>
  );
}
